import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Search, Check, X, ChevronRight, 
  Database, Grid, List, Filter, Download, Upload, 
  MoreVertical, Eye, EyeOff, Clock, Calendar,
  Tag, Settings, Sparkles, TrendingUp, MapPin
} from 'lucide-react';
import { MASTER_DATA_CATEGORIES, getMasterData, saveMasterData, MasterDataItem } from '../../lib/masterData';
import { masterDataAPI, type MasterDataType } from '../../lib/masterDataApi';
import { useToast } from '../ui/Toast';

export function MasterDataManager() {
  const [selectedCategory, setSelectedCategory] = useState(MASTER_DATA_CATEGORIES[0].key);
  const [items, setItems] = useState<MasterDataItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingItem, setEditingItem] = useState<MasterDataItem | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [countryOptions, setCountryOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [stateOptions, setStateOptions] = useState<Array<{ id: string; name: string; countryId?: string }>>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const { success, error } = useToast();

  const category = MASTER_DATA_CATEGORIES.find(c => c.key === selectedCategory);

  const categoryToApiType = (key: string): MasterDataType | null => {
    const map: Record<string, MasterDataType> = {
      userRoles: 'user-roles',
      cuisineTypes: 'cuisine-types',
      mealTypes: 'meal-types',
      foodCategories: 'food-categories',
      dietaryTags: 'dietary-tags',
      spiceLevels: 'spice-levels',
      orderStatuses: 'order-statuses',
      paymentMethods: 'payment-methods',
      paymentStatuses: 'payment-statuses',
      deliveryStatuses: 'delivery-statuses',
      amenities: 'amenities',
      tableStatuses: 'table-statuses',
      reservationStatuses: 'reservation-statuses',
      discountTypes: 'discount-types',
      offerCategories: 'offer-categories',
      reviewRatings: 'review-ratings',
      complaintCategories: 'complaint-categories',
      departments: 'departments',
      seatingPreferences: 'seating-preferences',
      countries: 'countries',
      states: 'states',
      cities: 'cities',
      timezones: 'timezones',
      currencies: 'currencies',
      languages: 'languages',
      priceRanges: 'price-ranges',
    };
    return map[key] || null;
  };

  useEffect(() => {
    void loadItems();
  }, [selectedCategory]);

  useEffect(() => {
    const loadLocationOptions = async () => {
      if (!isEditing) return;
      if (selectedCategory !== 'states' && selectedCategory !== 'cities') return;

      const countries = await masterDataAPI.getAll('countries', { isActive: true });
      const mappedCountries = (countries || []).map((c: any) => ({ id: c.id, name: c.name }));
      setCountryOptions(mappedCountries);

      if (selectedCategory === 'cities') {
        const params = formData.countryId ? { isActive: true, countryId: formData.countryId } : { isActive: true };
        const states = await masterDataAPI.getAll('states', params);
        const mappedStates = (states || []).map((s: any) => ({
          id: s.id,
          name: s.name,
          countryId: s.metadata?.countryId,
        }));
        setStateOptions(mappedStates);
      }
    };
    void loadLocationOptions();
  }, [selectedCategory, isEditing, formData.countryId]);

  const loadItems = async () => {
    const apiType = categoryToApiType(selectedCategory);
    if (apiType) {
      try {
        const data = await masterDataAPI.getAll(apiType);
        const normalized: MasterDataItem[] = (data || []).map((item: any) => ({
          // Parse metadata if API returns JSON string (master_data table).
          ...(typeof item.metadata === 'string'
            ? (() => {
                try {
                  return { metadata: JSON.parse(item.metadata) };
                } catch {
                  return { metadata: {} };
                }
              })()
            : {}),
          id: item.id,
          name: item.name,
          code: item.code,
          description: item.description,
          isActive: item.isActive !== false,
          order: item.displayOrder || 0,
          metadata: {
            ...(item.metadata || {}),
            ...(item.color ? { color: item.color } : {}),
            ...(item.levelValue !== undefined && item.levelValue !== null ? { level: item.levelValue } : {}),
          },
          createdAt: item.createdAt || new Date().toISOString(),
          updatedAt: item.updatedAt || new Date().toISOString(),
        }));
        setItems(normalized.sort((a, b) => (a.order || 0) - (b.order || 0)));
      } catch {
        error('Failed to load data from API');
        setItems([]);
      }
      return;
    }

    const data = getMasterData(selectedCategory);
    setItems(data.sort((a, b) => (a.order || 0) - (b.order || 0)));
  };

  const handleAdd = () => {
    setIsEditing(true);
    setEditingItem(null);
    const initialData: any = { isActive: true };
    category?.fields.forEach(field => {
      initialData[field.name] = '';
    });
    setFormData(initialData);
  };

  const handleEdit = (item: MasterDataItem) => {
    setIsEditing(true);
    setEditingItem(item);
    const data: any = { ...item };
    category?.fields.forEach(field => {
      if (field.name === 'color' && item.metadata?.color) {
        data[field.name] = item.metadata.color;
      } else if (field.name === 'level' && item.metadata?.level) {
        data[field.name] = item.metadata.level;
      } else if (field.name === 'symbol' && item.metadata?.symbol) {
        data[field.name] = item.metadata.symbol;
      } else if (field.name === 'countryId' && item.metadata?.countryId) {
        data[field.name] = item.metadata.countryId;
      } else if (field.name === 'stateId' && item.metadata?.stateId) {
        data[field.name] = item.metadata.stateId;
      } else if (field.name === 'state' && item.metadata?.state) {
        data[field.name] = item.metadata.state;
      } else if (field.name === 'country' && item.metadata?.country) {
        data[field.name] = item.metadata.country;
      }
    });
    setFormData(data);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this item?')) {
      const apiType = categoryToApiType(selectedCategory);
      if (apiType) {
        try {
          await masterDataAPI.delete(apiType, id);
          await loadItems();
          success('Item deleted successfully');
        } catch {
          error('Delete failed');
        }
      } else {
        const updated = items.filter(item => item.id !== id);
        saveMasterData(selectedCategory, updated);
        await loadItems();
        success('Item deleted successfully');
      }
    }
  };

  const handleToggleActive = async (id: string) => {
    const apiType = categoryToApiType(selectedCategory);
    if (apiType) {
      const current = items.find((item) => item.id === id);
      if (!current) return;
      try {
        await masterDataAPI.update(apiType, id, { isActive: !current.isActive });
        await loadItems();
        success(`Item ${!current.isActive ? 'activated' : 'deactivated'}`);
      } catch {
        error('Status update failed');
      }
      return;
    }

    const updated = items.map(item =>
      item.id === id ? { ...item, isActive: !item.isActive, updatedAt: new Date().toISOString() } : item
    );
    saveMasterData(selectedCategory, updated);
    await loadItems();
    success(`Item ${updated.find(i => i.id === id)?.isActive ? 'activated' : 'deactivated'}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newItem: MasterDataItem = {
      id: editingItem?.id || `${Date.now()}`,
      name: formData.name,
      code: formData.code,
      description: formData.description,
      isActive: formData.isActive !== false,
      order: editingItem?.order || items.length + 1,
      createdAt: editingItem?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      metadata: {}
    };

    // Handle special fields
    if (formData.color) newItem.metadata!.color = formData.color;
    if (formData.level) newItem.metadata!.level = formData.level;
    if (formData.symbol) newItem.metadata!.symbol = formData.symbol;
    if (formData.state) newItem.metadata!.state = formData.state;
    if (formData.country) newItem.metadata!.country = formData.country;

    const apiType = categoryToApiType(selectedCategory);
    if (apiType) {
      try {
        let payload: any = {};
        if (selectedCategory === 'countries') {
          payload = {
            code: (formData.code || formData.name || '').toString().trim().toUpperCase().replace(/[\s-]+/g, '_'),
            name: formData.name,
            isActive: formData.isActive !== false,
          };
        } else if (selectedCategory === 'states') {
          payload = {
            code: (formData.code || formData.name || '').toString().trim().toUpperCase().replace(/[\s-]+/g, '_'),
            name: formData.name,
            countryId: formData.countryId,
            isActive: formData.isActive !== false,
          };
        } else if (selectedCategory === 'cities') {
          payload = {
            name: formData.name,
            countryId: formData.countryId,
            stateId: formData.stateId,
            isActive: formData.isActive !== false,
          };
        } else if (selectedCategory === 'foodCategories') {
          payload = {
            name: formData.name,
            description: formData.description,
            isActive: formData.isActive !== false,
            displayOrder: editingItem?.order || items.length + 1,
          };
        } else if (selectedCategory === 'seatingPreferences') {
          payload = {
            code: (formData.code || formData.name || '').toString().trim().toUpperCase().replace(/[\s-]+/g, '_'),
            name: formData.name,
            isActive: formData.isActive !== false,
          };
        } else {
          payload = {
            code: (formData.code || formData.name || '').toString().trim().toUpperCase().replace(/[\s-]+/g, '_'),
            name: formData.name,
            description: formData.description,
            isActive: formData.isActive !== false,
            displayOrder: editingItem?.order || items.length + 1,
          };
          if (formData.color) payload.color = formData.color;
          if (formData.level !== undefined && formData.level !== '') payload.level = Number(formData.level);
          const metadata: Record<string, any> = {};
          if (formData.symbol) metadata.symbol = formData.symbol;
          if (formData.state) metadata.state = formData.state;
          if (formData.country) metadata.country = formData.country;
          if (Object.keys(metadata).length > 0) payload.metadata = JSON.stringify(metadata);
        }

        if (editingItem) {
          await masterDataAPI.update(apiType, editingItem.id, payload);
        } else {
          await masterDataAPI.create(apiType, payload);
        }

        await loadItems();
        setIsEditing(false);
        setEditingItem(null);
        success(editingItem ? 'Item updated successfully' : 'Item added successfully');
      } catch {
        error('Save failed. Check API/auth/duplicate code.');
      }
      return;
    }

    let updated: MasterDataItem[];
    if (editingItem) {
      updated = items.map(item => item.id === editingItem.id ? newItem : item);
    } else {
      updated = [...items, newItem];
    }

    saveMasterData(selectedCategory, updated);
    await loadItems();
    setIsEditing(false);
    setEditingItem(null);
    success(editingItem ? 'Item updated successfully' : 'Item added successfully');
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.code && item.code.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = filterActive === 'all' ? true : 
      filterActive === 'active' ? item.isActive : !item.isActive;
    
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: items.length,
    active: items.filter(i => i.isActive).length,
    inactive: items.filter(i => !i.isActive).length
  };

  return (
    <div className="flex gap-6 h-full">
      {/* Enhanced Category Sidebar */}
      <div className="w-80 bg-gradient-to-br from-purple-50 via-white to-blue-50 rounded-2xl shadow-lg border border-purple-100/50 p-6">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
              <Database className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-gray-900 font-bold text-lg">Master Data</h3>
              <p className="text-gray-500 text-xs">Configuration Hub</p>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-3 gap-2 mb-6 p-3 bg-white/60 backdrop-blur-sm rounded-xl border border-purple-100">
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">{stats.total}</div>
            <div className="text-xs text-gray-600">Total</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-green-600">{stats.active}</div>
            <div className="text-xs text-gray-600">Active</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-red-600">{stats.inactive}</div>
            <div className="text-xs text-gray-600">Inactive</div>
          </div>
        </div>

        <div className="space-y-1 max-h-[calc(100vh-350px)] overflow-y-auto custom-scrollbar">
          {MASTER_DATA_CATEGORIES.map((cat) => {
            return (
              <button
                key={cat.key}
                onClick={() => {
                  setSelectedCategory(cat.key);
                  setIsEditing(false);
                  setSearchTerm('');
                }}
                className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl text-left transition-all duration-200 group ${
                  selectedCategory === cat.key
                    ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg shadow-purple-500/30 scale-[1.02]'
                    : 'text-gray-700 hover:bg-white/70 hover:shadow-sm'
                }`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{cat.label}</div>
                  </div>
                </div>
                {selectedCategory === cat.key && (
                  <ChevronRight className="w-4 h-4 flex-shrink-0 animate-pulse" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Enhanced Content Area */}
      <div className="flex-1 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        {!isEditing ? (
          <>
            {/* Modern Header */}
            <div className="bg-gradient-to-r from-purple-500 via-purple-600 to-blue-600 px-8 py-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div>
                    <h2 className="text-2xl font-bold mb-1">{category?.label}</h2>
                    <p className="text-purple-100 text-sm">{category?.description}</p>
                  </div>
                </div>
                <button
                  onClick={handleAdd}
                  className="flex items-center gap-2 px-6 py-3 bg-white text-purple-600 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-200 font-semibold"
                >
                  <Plus className="w-5 h-5" />
                  Add New
                </button>
              </div>

              {/* Search and Filters */}
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-300" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={selectedCategory === 'foodCategories' ? 'Search by name...' : 'Search by name or code...'}
                    className="w-full pl-12 pr-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white placeholder-purple-200 focus:outline-none focus:ring-2 focus:ring-white/50"
                  />
                </div>

                <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-xl p-1 border border-white/30">
                  <button
                    onClick={() => setFilterActive('all')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      filterActive === 'all'
                        ? 'bg-white text-purple-600 shadow-lg'
                        : 'text-white hover:bg-white/20'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setFilterActive('active')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      filterActive === 'active'
                        ? 'bg-white text-green-600 shadow-lg'
                        : 'text-white hover:bg-white/20'
                    }`}
                  >
                    Active
                  </button>
                  <button
                    onClick={() => setFilterActive('inactive')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      filterActive === 'inactive'
                        ? 'bg-white text-red-600 shadow-lg'
                        : 'text-white hover:bg-white/20'
                    }`}
                  >
                    Inactive
                  </button>
                </div>

                <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm rounded-xl p-1 border border-white/30">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-all ${
                      viewMode === 'grid'
                        ? 'bg-white text-purple-600 shadow-lg'
                        : 'text-white hover:bg-white/20'
                    }`}
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-all ${
                      viewMode === 'list'
                        ? 'bg-white text-purple-600 shadow-lg'
                        : 'text-white hover:bg-white/20'
                    }`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Items Display */}
            <div className="p-8 max-h-[calc(100vh-320px)] overflow-y-auto custom-scrollbar">
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className={`group relative p-5 rounded-xl border-2 transition-all duration-200 hover:shadow-xl hover:-translate-y-1 ${
                        item.isActive
                          ? 'bg-gradient-to-br from-white to-purple-50/30 border-purple-200 hover:border-purple-400'
                          : 'bg-gray-50 border-gray-200 opacity-75'
                      }`}
                    >
                      {/* Status Badge */}
                      <div className="absolute top-3 right-3 flex items-center gap-2">
                        <button
                          onClick={() => handleToggleActive(item.id)}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${
                            item.isActive
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                        >
                          {item.isActive ? '● Active' : '○ Inactive'}
                        </button>
                      </div>

                      {/* Content */}
                      <div className="mb-4 pr-24">
                        <div className="flex items-start gap-3">
                          {item.metadata?.color && (
                            <div
                              className="w-10 h-10 rounded-xl shadow-md flex-shrink-0 border-2 border-white"
                              style={{ backgroundColor: item.metadata.color }}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-gray-900 font-bold text-lg mb-1 truncate">
                              {item.name}
                            </h3>
                            <div className="flex items-center gap-2 flex-wrap">
                              {item.code && (
                                <span className="px-2.5 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-semibold">
                                  {item.code}
                                </span>
                              )}
                              {item.metadata?.symbol && (
                                <span className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold">
                                  {item.metadata.symbol}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {item.description && (
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {item.description}
                        </p>
                      )}

                      {item.metadata?.state && (
                        <p className="text-gray-500 text-xs mb-3 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {item.metadata.state}, {item.metadata.country}
                        </p>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
                        <button
                          onClick={() => handleEdit(item)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors text-sm font-medium"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="flex items-center justify-center gap-2 px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Hover Effect Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-blue-500/0 group-hover:from-purple-500/5 group-hover:to-blue-500/5 rounded-xl pointer-events-none transition-all duration-200" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-lg ${
                        item.isActive
                          ? 'bg-white border-purple-200 hover:border-purple-400'
                          : 'bg-gray-50 border-gray-200 opacity-75'
                      }`}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        {item.metadata?.color && (
                          <div
                            className="w-3 h-12 rounded-lg shadow-sm flex-shrink-0"
                            style={{ backgroundColor: item.metadata.color }}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <p className="text-gray-900 font-bold text-lg">{item.name}</p>
                            {item.code && (
                              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-semibold">
                                {item.code}
                              </span>
                            )}
                            {item.metadata?.symbol && (
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold">
                                {item.metadata.symbol}
                              </span>
                            )}
                          </div>
                          {item.description && (
                            <p className="text-gray-600 text-sm">{item.description}</p>
                          )}
                          {item.metadata?.state && (
                            <p className="text-gray-500 text-xs mt-1">
                              📍 {item.metadata.state}, {item.metadata.country}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 flex-shrink-0">
                        <button
                          onClick={() => handleToggleActive(item.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                            item.isActive
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                        >
                          {item.isActive ? 'Active' : 'Inactive'}
                        </button>
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-2.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {filteredItems.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Database className="w-10 h-10 text-purple-400" />
                  </div>
                  <h3 className="text-gray-900 font-bold text-xl mb-2">
                    {searchTerm ? 'No items found' : 'No items yet'}
                  </h3>
                  <p className="text-gray-500 mb-6">
                    {searchTerm 
                      ? 'Try adjusting your search or filters' 
                      : 'Get started by adding your first item'}
                  </p>
                  {!searchTerm && (
                    <button
                      onClick={handleAdd}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl hover:shadow-xl transition-all"
                    >
                      <Plus className="w-5 h-5" />
                      Add First Item
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Enhanced Edit Form */}
            <div className="bg-gradient-to-r from-purple-500 via-purple-600 to-blue-600 px-8 py-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    {editingItem ? <Edit className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">
                      {editingItem ? 'Edit' : 'Add New'} {category?.label}
                    </h2>
                    <p className="text-purple-100 text-sm">Fill in the details below</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditingItem(null);
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-8 max-h-[calc(100vh-180px)] overflow-y-auto custom-scrollbar">
              <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 border-2 border-purple-200 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {category?.fields.map((field) => (
                      <div key={field.name} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                        <label className="block text-gray-900 text-sm font-bold mb-3">
                          {field.label} {field.required && <span className="text-red-500">*</span>}
                        </label>
                        {field.type === 'textarea' ? (
                          <textarea
                            required={field.required}
                            value={formData[field.name] || ''}
                            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                            rows={4}
                            className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none bg-white"
                            placeholder={`Enter ${field.label.toLowerCase()}...`}
                          />
                        ) : field.type === 'color' ? (
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={formData[field.name] || '#000000'}
                              onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                              className="w-16 h-12 border-2 border-purple-200 rounded-xl cursor-pointer"
                            />
                            <input
                              type="text"
                              value={formData[field.name] || ''}
                              onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                              placeholder="#000000"
                              className="flex-1 px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                            />
                          </div>
                        ) : field.type === 'number' ? (
                          <input
                            type="number"
                            required={field.required}
                            value={formData[field.name] || ''}
                            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                            placeholder={`Enter ${field.label.toLowerCase()}...`}
                          />
                        ) : field.type === 'select' ? (
                          <select
                            required={field.required}
                            value={formData[field.name] || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (field.name === 'countryId' && selectedCategory === 'cities') {
                                setFormData({ ...formData, countryId: value, stateId: '' });
                                return;
                              }
                              setFormData({ ...formData, [field.name]: value });
                            }}
                            className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                          >
                            <option value="">Select {field.label.toLowerCase()}...</option>
                            {(field.name === 'countryId' ? countryOptions : field.name === 'stateId' ? stateOptions : (field.options || []).map((o) => ({ id: o, name: o }))).map((option: any) => (
                              <option key={option.id} value={option.id}>
                                {option.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            required={field.required}
                            value={formData[field.name] || ''}
                            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                            placeholder={`Enter ${field.label.toLowerCase()}...`}
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 pt-6 border-t-2 border-purple-200">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={formData.isActive !== false}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          className="w-6 h-6 text-purple-600 border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 cursor-pointer"
                        />
                      </div>
                      <div>
                        <span className="text-gray-900 font-bold text-lg">Active Status</span>
                        <p className="text-gray-600 text-sm">Enable this item for use in the system</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-200 font-bold text-lg"
                  >
                    <Check className="w-5 h-5" />
                    {editingItem ? 'Save Changes' : 'Create Item'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(false);
                      setEditingItem(null);
                    }}
                    className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-bold text-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </>
        )}
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #a855f7, #3b82f6);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #9333ea, #2563eb);
        }
      `}</style>
    </div>
  );
}


